// @flow

import React, { Fragment } from 'react';

import Link from 'app/common/components/Link';
import help from 'app/developer-v3/components/PartnerSupportForm/components/blocks/Help';
import radio from 'app/developer-v3/components/PartnerSupportForm/components/blocks/Radio';
import {
  BASE_DOC_URL_VB,
  REBRANDING_GUIDE_URL,
} from 'app/developer-v3/constants';
import { createRebrandingContact } from 'app/developer-v3/components/PartnerSupportForm/content/nodes/common/defaultContact';
import {
  getUserFields,
  messageField,
} from 'app/developer-v3/components/PartnerSupportForm/content/nodes/common';

// eslint-disable-next-line no-unused-vars
export const createMaintainingMyApp = (isLoggedIn: boolean) => ({
  value: 'maintaining',
  label: 'Maintaining or enhancing my integration',
  zendeskCustomFieldValue: 'pcf-existing-maintaining',
  child: radio({
    id: 'applying',
    label: "Let's get you some help",
    zendeskCustomFieldId: 360044581992,
    items: [
      {
        value: 'rebranding',
        label: 'Rebrand my integration',
        zendeskCustomFieldValue: 'pcf-existing-maintain-rebranding',
        child: help({
          // TODO: override the default help contact page
          docs: {
            title: 'Rebranding your Integration',
            body: () => (
              <Fragment>
                <p>
                  Your customers already recognize your app name and logo, and
                  they’ll look for it inside Zapier. You can set the App Name,
                  Logo, Description, Category, and Colors for your app by
                  following this{' '}
                  <Link
                    href={`${BASE_DOC_URL_VB}/publish/branding-guidelines`}
                    target="_blank"
                  >
                    Zapier Branding and Design Guidelines Guide
                  </Link>
                  .
                </p>
                <p>
                  If your integration is <strong>private</strong>, you can
                  update those details at any time.
                </p>

                <p>
                  If your integration is <strong>public</strong>, to change
                  those details, please submit this form:{' '}
                  <Link href={REBRANDING_GUIDE_URL} target="_blank">
                    Rebrand Request
                  </Link>
                  .
                </p>
              </Fragment>
            ),
          },
          contact: createRebrandingContact({
            media: {
              label: `Please upload your new logo.`,
            },
          }),
        }),
      },
      {
        value: 'creating-new-version',
        label: 'Creating a new version of my integration',
        zendeskCustomFieldValue: 'pcf-existing-creating-new-version',
        child: help({
          docs: {
            title: 'Creating a new version of your existing integration',
            body: () => (
              <Fragment>
                <p>
                  When creating a new version of your Zapier integration, you
                  never need to start over. Instead, to make a new version,
                  start by cloning an existing version. That lets you start with
                  an exact copy of your integration where you can make the
                  changes and additions you need.
                </p>
                <p>
                  To clone a version, click the gear icon in the <i>Versions</i>{' '}
                  page, and select <i>Clone</i>. In the dialog, select the
                  version to clone from (usually the most recent version, unless
                  you want to roll back recent changes). Then choose what type
                  of version you want to create:
                </p>
                <ul>
                  <li>
                    <b>Patch</b>: A <code>x.x.1</code> version, typically used
                    to fix bugs and issues in existing integrations
                  </li>
                  <li>
                    <b>Minor</b>: A <code>x.1.x</code> version, typically to add
                    new minor features to existing integrations
                  </li>
                  <li>
                    <b>Major</b>: A <code>2.x.x</code> version to launch a full
                    new version of your integration with new triggers and or
                    actions
                  </li>
                </ul>
                <p>
                  Once you clone the integration, click the <i>Edit</i> button
                  to tweak the new version of your integration, or click{' '}
                  <i>Close</i> to go back to the <i>Versions</i> page.
                </p>
              </Fragment>
            ),
            links: [
              {
                href: `${BASE_DOC_URL_VB}/manage/versions`,
                title: 'Manage integration versions in Platform UI',
              },
              {
                href: `${BASE_DOC_URL_VB}/reference/cli-docs#deploying-an-integration-version`,
                title: 'Manage integration versions in Platform CLI',
              },
              {
                href: `${BASE_DOC_URL_VB}/reference/cloning-a-version-tutorial`,
                title: 'Cloning a version (video tutorial)',
              },
            ],
          },
          contact: [
            ...getUserFields(isLoggedIn),
            {
              ...messageField,
              details: `Please let us know what questions you have about creating new integration versions.`,
            },
          ],
        }),
      },
      {
        value: 'promoting-version',
        label: 'Making a new version public',
        zendeskCustomFieldValue: 'pcf-existing-promoting-version',
        child: help({
          docs: {
            title: 'Promoting a new version of your integration',
            body: () => (
              <Fragment>
                <p>
                  Promoting your new integration version makes it the default
                  version that Zapier shows to users when creating new Zaps.
                  Select the <i>Promote</i> button in your version settings to
                  mark an integration as the new default version.
                </p>
                <p>
                  If you’re working with an integration that’s currently
                  private, the <i>Promote</i> option isn’t used. Instead, make
                  sure users you’d like to test or move to the new integration
                  version are{' '}
                  <Link
                    href={`${BASE_DOC_URL_VB}/manage/sharing`}
                    target="_blank"
                  >
                    invited
                  </Link>{' '}
                  to that version, and encourage them to try it out.
                </p>
                <p>
                  <strong>Remember</strong>: After promoting, as long as you've
                  not made{' '}
                  <Link
                    href={`${BASE_DOC_URL_VB}/manage/planning-changes#effects-of-different-changes-versioning-matrix`}
                    target="_blank"
                  >
                    breaking changes
                  </Link>{' '}
                  between versions, you can{' '}
                  <Link
                    href={`${BASE_DOC_URL_VB}/manage/migrate`}
                    target="_blank"
                  >
                    migrate
                  </Link>{' '}
                  users and their Zaps to the latest version.
                </p>
              </Fragment>
            ),
            links: [
              {
                href: `${BASE_DOC_URL_VB}/manage/promote`,
                title: 'Promote an integration version in the UI',
              },
              {
                href: `${BASE_DOC_URL_VB}/reference/cli-docs#promoting-an-integration-version`,
                title: 'Promoting an integration version in the CLI',
              },
              {
                href: `${BASE_DOC_URL_VB}/reference/promoting-a-version-tutorial`,
                title: 'Promoting a version (video tutorial)',
              },
            ],
          },
          contact: [
            ...getUserFields(isLoggedIn),
            {
              ...messageField,
              details: `Please let us know what questions you have about creating new integration versions.`,
            },
          ],
        }),
      },
      {
        value: 'migrating-users',
        label: 'Migrating users',
        zendeskCustomFieldValue: 'pcf-existing-migrating-users',
        child: help({
          docs: {
            title: 'Migrate Users to Your New Integration Version',
            body: () => (
              <Fragment>
                <p>
                  Once you’ve added all the changes needed to your integration,
                  you can start rolling it out to users to update their existing
                  Zaps and let users make new Zaps with your new integration
                  features.
                </p>
                <p>
                  If you made{' '}
                  <Link
                    href={`${BASE_DOC_URL_VB}/manage/planning-changes#effects-of-different-changes-versioning-matrix`}
                    target="_blank"
                  >
                    non-breaking changes
                  </Link>
                  , you can migrate existing users and Zaps to your new
                  integration version. Select the <i>Migrate</i> option on your
                  new integration version, then choose which older version of
                  your integration to move users from, and the new version to
                  move them to. Finally, choose what percentage of users to
                  migrate.
                </p>
              </Fragment>
            ),
            links: [
              {
                href: `${BASE_DOC_URL_VB}/manage/migrate`,
                title: 'Migrating users in the Platform UI',
              },
              {
                href: `${BASE_DOC_URL_VB}/reference/cli-docs#deploying-an-integration-version`,
                title: 'Migrating users in the Platform CLI',
              },
            ],
          },
          contact: [
            ...getUserFields(isLoggedIn),
            {
              ...messageField,
              details: `Please let us know what questions you have about migrating users.`,
            },
          ],
        }),
      },
      {
        value: 'managing-my-team',
        label: 'Managing my team',
        zendeskCustomFieldValue: 'pcf-existing-managing-my-team',
        child: help({
          docs: {
            title: 'How to Invite Others to Test New Integrations',
            body: () => (
              <Fragment>
                <p>
                  While developing your app integration, invite additional
                  admins from your integration’s <i>Manage Team</i> page. These
                  users will have full access to the Platform UI to edit the app
                  details, authentication, triggers, actions, and versions. This
                  is the best way to add additional developers to your project.
                </p>
                <p>
                  You can also invite other team members as <i>collaborators</i>
                  , which gives them view-only access. Collaborators can view
                  performance data, view feature requests and bugs, and access
                  tools to embed Zapier integrations inside your UI.
                  Collaborator access is recommended for marketers, product
                  managers, and others not contributing directly to the creation
                  and maintenance of the integration.
                </p>
              </Fragment>
            ),
            links: [
              {
                href: `${BASE_DOC_URL_VB}/manage/add-team`,
                title: 'Add team members to integration',
              },
              {
                href: `${BASE_DOC_URL_VB}/reference/managing-team-tutorial`,
                title: 'Managing integration team members (video tutorial)',
              },
            ],
          },
          contact: [
            ...getUserFields(isLoggedIn),
            {
              ...messageField,
              details: `Please let us know what questions you have about migrating users.`,
            },
          ],
        }),
      },
    ],
  }),
});
